import { motion } from "framer-motion";
import { Wallet, Building2, Store, ArrowRight, CheckCircle2 } from "lucide-react";

const financialPartners = [
  { name: "Cash Advance America", sub: "Short-term liquidity",  role: "Lending Partner",     color: "#17A673", initial: "CA" },
  { name: "Zelle",                sub: "Bank-to-bank transfers",role: "Instant Payments",    color: "#6D1ED4", initial: "Z" },
  { name: "Cash App",             sub: "Send · Receive · Save", role: "Digital Wallet",      color: "#00D64F", initial: "$" },
  { name: "Venmo",                sub: "Social payments",       role: "Digital Wallet",      color: "#3D95CE", initial: "V" },
  { name: "Chime Pay",            sub: "Fee-free banking",      role: "Digital Wallet",      color: "#1EC677", initial: "C" },
  { name: "Apple Pay",            sub: "Tap · Face ID · Watch", role: "Contactless",         color: "#F5F5F7", initial: "" },
];

const retailPartners = [
  { name: "Walmart",       tag: "5,300+ US stores",   short: "W" },
  { name: "Dollar General",tag: "19,000+ locations",   short: "DG" },
  { name: "CVS",           tag: "9,900+ pharmacies",   short: "CVS" },
  { name: "Walgreens",     tag: "8,700+ pharmacies",   short: "W" },
];

function AppleLogo({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 384 512" fill="currentColor" className={className} aria-hidden>
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

function PartnerCard({ p, i }) {
  const isApple = p.name === "Apple Pay";
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: i * 0.05, duration: 0.5 }}
      whileHover={{ y: -4 }}
      className="ef-card p-5 flex items-center gap-4"
      data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div
        className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 font-display font-bold text-lg"
        style={{
          background: isApple ? "#0A0A0A" : `${p.color}22`,
          border: `1px solid ${p.color}55`,
          color: p.color,
        }}
      >
        {isApple ? <AppleLogo className="h-6 w-6 text-white" /> : (p.initial || p.name[0])}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{p.role}</div>
        <div className="font-display font-bold text-lg leading-tight truncate">{p.name}</div>
        <div className="text-xs text-white/55 truncate">{p.sub}</div>
      </div>
    </motion.div>
  );
}

function RetailCard({ r, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: i * 0.06 }}
      whileHover={{ y: -3, borderColor: "rgba(212,175,55,0.5)" }}
      className="rounded-xl border border-white/10 bg-white/[0.02] p-5 flex flex-col items-start gap-3 transition-colors"
      data-testid={`retail-${r.name.toLowerCase().replace(/[\s-]+/g, "-")}`}
    >
      <div className="h-11 w-11 rounded-lg bg-gradient-to-br from-white/10 to-white/[0.02] border border-white/10 flex items-center justify-center font-display font-bold text-white/90 text-base">
        {r.short}
      </div>
      <div>
        <div className="font-display font-bold text-base">{r.name}</div>
        <div className="text-[11px] text-white/50 mt-0.5">{r.tag}</div>
      </div>
    </motion.div>
  );
}

export default function Partners() {
  return (
    <section id="partners" className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 ef-hero-radial opacity-40" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="ef-overline">Trusted Network</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mt-4">
              Move money the way <em className="ef-gold-text not-italic">you already do.</em>
            </h2>
            <p className="mt-4 text-white/60 max-w-xl">
              Elevate connects to the platforms and places you already trust — from digital wallets to your neighborhood corner store. Fund, transfer, and withdraw seamlessly across our nationwide partner network.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-6 text-xs font-mono uppercase tracking-widest text-white/50">
            <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#17A673]" /> Real-time</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#17A673]" /> Zero-fee options</div>
          </div>
        </div>

        {/* Financial + Payment Partners */}
        <div className="mb-14">
          <div className="flex items-center gap-2 mb-6">
            <Building2 className="h-4 w-4 text-[#1B63F0]" />
            <div className="ef-overline">Banking & Payment Partners</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {financialPartners.map((p, i) => (
              <PartnerCard key={p.name} p={p} i={i} />
            ))}
          </div>
        </div>

        {/* Retail Cash Network */}
        <div>
          <div className="flex items-center gap-2 mb-6">
            <Store className="h-4 w-4 text-[#D4AF37]" />
            <div className="ef-overline">Cash Pickup & Deposit Network</div>
          </div>
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#0B1D3A]/60 to-transparent p-6 md:p-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {retailPartners.map((r, i) => (
                <RetailCard key={r.name} r={r} i={i} />
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#17A673]/15 border border-[#17A673]/40 flex items-center justify-center">
                  <Wallet className="h-4 w-4 text-[#17A673]" />
                </div>
                <div>
                  <div className="text-sm font-semibold">Deposit or withdraw at 45,000+ retail locations nationwide</div>
                  <div className="text-xs text-white/55 mt-0.5">No card fees at Elevate partner retailers · Available where locations allow.</div>
                </div>
              </div>
              <a href="#faq" data-testid="partners-learn-more" className="inline-flex items-center gap-1.5 text-sm text-[#D4AF37] hover:text-white transition-colors">
                Learn how it works <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>

        {/* Disclosure */}
        <p className="mt-8 text-[11px] text-white/40 leading-relaxed max-w-4xl">
          Partner logos and names are trademarks of their respective owners. Availability of specific features (e.g., Zelle, Cash App, Venmo, Chime Pay, or Apple Pay) depends on your account type, verification status, and each partner's individual terms. Retail cash-network fees, where applicable, are disclosed at the point of transaction.
        </p>
      </div>
    </section>
  );
}
